import { Chip, ChipInput, ChipList, ChipRemove } from '@components'
import { InputWrapper } from '@components/Inputs/Input'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaTimes } from 'react-icons/fa'
import styled from 'styled-components'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface Props {
  inputId: string
  label: string
  info?: string
  placeholder?: string
  error?: string
  recipients: string[]
  onChange: (recipients: string[]) => void
}

export const EinvoicingRecipientsField: React.FC<Props> = ({
  inputId,
  label,
  info,
  placeholder,
  error,
  recipients,
  onChange
}) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')
  const [localError, setLocalError] = useState<string>()
  const [isFocused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Mirrors `draft` so blur, which runs after Escape, sees the cleared value.
  const pendingDraft = useRef('')

  const updateDraft = (next: string) => {
    pendingDraft.current = next
    setDraft(next)
  }

  // Enter / comma / blur: keep valid addresses, leave invalid ones in the draft.
  const commitDraft = () => {
    const next = [...recipients]
    const invalid: string[] = []
    for (const part of pendingDraft.current.split(/[\s,;]+/)) {
      const address = part.trim().toLowerCase()
      if (!address) continue
      if (EMAIL_PATTERN.test(address)) {
        if (!next.includes(address)) next.push(address)
      } else {
        invalid.push(address)
      }
    }
    if (next.length !== recipients.length) onChange(next)
    if (invalid.length > 0) {
      updateDraft(invalid.join(', '))
      setLocalError(t('settings.einvoicing.share.send.invalidEmail'))
      return
    }
    updateDraft('')
    setLocalError(undefined)
  }

  const removeRecipient = (address: string) => {
    onChange(recipients.filter(recipient => recipient !== address))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commitDraft()
    } else if (event.key === 'Backspace' && !draft && recipients.length > 0) {
      removeRecipient(recipients[recipients.length - 1])
    } else if (event.key === 'Escape') {
      updateDraft('')
      setLocalError(undefined)
    }
  }

  const shownError = localError ?? error

  return (
    <InputWrapper
      inputId={inputId}
      label={label}
      info={info}
      required
      isFocused={isFocused}
      isInvalid={Boolean(shownError)}
      errorMsg={shownError}
    >
      <TallChipList onClick={() => inputRef.current?.focus()}>
        {recipients.map(address => (
          <Chip key={address}>
            <span>{address}</span>
            <ChipRemove type="button" aria-label={address} onClick={() => removeRecipient(address)}>
              <FaTimes />
            </ChipRemove>
          </Chip>
        ))}
        <ChipInput
          id={inputId}
          ref={inputRef}
          // Chrome ignores "off" for fields it classifies as email; an unknown token disables it.
          autoComplete="einvoicing-recipient"
          data-1p-ignore
          data-lpignore="true"
          value={draft}
          placeholder={recipients.length === 0 ? placeholder : undefined}
          onChange={event => {
            updateDraft(event.target.value)
            setLocalError(undefined)
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false)
            commitDraft()
          }}
        />
      </TallChipList>
    </InputWrapper>
  )
}

const TallChipList = styled(ChipList)`
  min-height: 100px;
  align-items: flex-start;
  align-content: flex-start;
  cursor: text;
`
